import React from 'react';
import EventEmitter from 'events';
import throttle from 'lodash/throttle';
import { LocalStorageService } from '@uplift/services';


export const USER_ACTIVITY_EVENT = 'lastUserActivity';


export class UserActivityMonitor {
  private emitter: EventEmitter;
  private eventTypes = [ 'keypress', 'click', 'mousemove', 'resize', 'scroll' ];

  private static _instance: UserActivityMonitor;
  public static getInstance() {
    if (!this._instance) {
      this._instance = new UserActivityMonitor();
      this._instance.enable();
    }
    return this._instance;
  }

  private constructor() {
    this.emitter = new EventEmitter();
  }

  private applyEvents = () => {
    this.handleActive();
    this.eventTypes.forEach(eventType => document.addEventListener(eventType, this.handleActive));
  };

  private removeEvents = () => {
    this.eventTypes.forEach(eventType => document.removeEventListener(eventType, this.handleActive));
    this.handleActive.cancel();
  };

  addListener = (callback: (v: number) => void) => {
    this.emitter.on(USER_ACTIVITY_EVENT, callback);
  };

  removeListener = (callback: (v: number) => void) => {
    this.emitter.off(USER_ACTIVITY_EVENT, callback);
  };

  handleActive = throttle(() => {
    this.emitter.emit(USER_ACTIVITY_EVENT, Date.now());
  }, 1000);

  disable = () => {
    window.removeEventListener('blur', this.removeEvents);
    window.removeEventListener('focus', this.applyEvents);
    this.removeEvents();
  };

  enable = () => {
    window.addEventListener('blur', this.removeEvents);
    window.addEventListener('focus', this.applyEvents);
    this.applyEvents();
  };
}


export const useUserActivityMonitor = (enabled: boolean) => {
  React.useEffect(() => {
    if (!enabled) return;

    const activityMonitor = UserActivityMonitor.getInstance();

    const userActivityHandler = (lastActivity: number) => {
      LocalStorageService.setItemWithEmit('lastUserActivity', lastActivity);
    };

    activityMonitor.addListener(userActivityHandler);

    // handle user activity when user open page
    userActivityHandler(Date.now());

    return () => {
      activityMonitor.removeListener(userActivityHandler);
    };
  }, [ enabled ]);
};
